import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactSelect from 'react-select';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import CachedImage from '../components/CachedImage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  BookOpen,
  BarChart3,
  Trash2,
  Loader2,
  FileQuestion,
  Target,
  Calendar,
  CheckSquare,
  School,
  MapPin,
  Image,
  Eye,
  Upload,
  X,
  Edit,
  Search,
  Filter,
  Sparkles,
  Wand2,
  GripVertical
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import RichText from '../components/RichText';
import { storage } from '../lib/firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Year options for past papers (2010-2024)
const YEARS = Array.from({ length: 15 }, (_, i) => 2024 - i);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [dailyPapers, setDailyPapers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [paperDate, setPaperDate] = useState('');
  const [newSchool, setNewSchool] = useState({ name: '', district: '' });
  const [newStream, setNewStream] = useState({ name: '' });
  const [newSubject, setNewSubject] = useState({ name: '', stream_ids: [] });
  const [newTopic, setNewTopic] = useState({ name: '', subject_id: '', order: 1 });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  // Question management states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  
  // AI Extraction states
  const [extracting, setExtracting] = useState(false);
  const [extractionFile, setExtractionFile] = useState(null);
  
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    options: [
      { text: '', image_url: '' },
      { text: '', image_url: '' },
      { text: '', image_url: '' },
      { text: '', image_url: '' },
      { text: '', image_url: '' }
    ],
    correct_option: 0,
    difficulty: 'medium',
    subject_id: '',
    topic_id: '',
    mode: 'model_paper',
    year: null,
    explanation: '',
    image_url: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, questionsRes, papersRes, schoolsRes, streamsRes, subjectsRes, topicsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/questions`),
        axios.get(`${API}/daily-papers`).catch(() => ({ data: [] })),
        axios.get(`${API}/schools`).catch(() => ({ data: [] })),
        axios.get(`${API}/streams`).catch(() => ({ data: [] })),
        axios.get(`${API}/subjects`).catch(() => ({ data: [] })),
        axios.get(`${API}/topics`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setQuestions(questionsRes.data);
      setDailyPapers(papersRes.data);
      setSchools(schoolsRes.data);
      setStreams(streamsRes.data);
      setSubjects(subjectsRes.data);
      setTopics(topicsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setUploadProgress(0);
    setNewQuestion({ ...newQuestion, image_url: '' });
  };

  const uploadImageToFirebase = async () => {
    if (!imageFile) return null;

    return new Promise((resolve, reject) => {
      const timestamp = Date.now();
      const filename = `${timestamp}_${imageFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const subject = subjects.find(s => s.id === newQuestion.subject_id)?.name || 'general';
      const storageRef = ref(storage, `questions/${subject}/${filename}`);
      
      const uploadTask = uploadBytesResumable(storageRef, imageFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error('Upload error:', error);
          toast.error('Failed to upload image');
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...newQuestion.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setNewQuestion({ ...newQuestion, options: newOptions });
  };

  const handleOptionImageUpload = async (index, file) => {
    if (!file) return;
    
    try {
      setUploading(true);
      toast.info(`Uploading option ${index + 1} image...`);
      
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const fileName = `option-images/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error('Upload error:', error);
          toast.error(`Failed to upload option ${index + 1} image`);
          setUploading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            handleOptionChange(index, 'image_url', downloadURL);
            toast.success(`Option ${index + 1} image uploaded`);
            setUploading(false);
            setUploadProgress(0);
          } catch (error) {
            console.error('Error getting download URL:', error);
            toast.error(`Failed to upload option ${index + 1} image`);
            setUploading(false);
          }
        }
      );
    } catch (error) {
      toast.error(`Failed to upload option ${index + 1} image`);
      console.error(error);
      setUploading(false);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    // Validate that each option has either text or image
    const invalidOption = newQuestion.options.some(o => !o.text?.trim() && !o.image_url?.trim());
    if (!newQuestion.text || !newQuestion.subject_id || invalidOption) {
      toast.error('Please fill all required fields. Each option must have text or an image.');
      return;
    }

    setSubmitting(true);
    setUploading(true);
    
    try {
      let imageUrl = newQuestion.image_url;

      // Upload image if a new file is selected
      if (imageFile) {
        toast.info('Uploading image...');
        imageUrl = await uploadImageToFirebase();
      }

      const questionData = {
        ...newQuestion,
        image_url: imageUrl || null,
        topic: newQuestion.topic || null,
        year: newQuestion.mode === 'past_paper' ? newQuestion.year : null
      };
      
      await axios.post(`${API}/questions`, questionData);
      toast.success('Question added successfully!');
      
      // Reset form
      setNewQuestion({
        text: '',
        options: [
          { text: '', image_url: '' },
          { text: '', image_url: '' },
          { text: '', image_url: '' },
          { text: '', image_url: '' },
          { text: '', image_url: '' }
        ],
        correct_option: 0,
        difficulty: 'medium',
        subject_id: '',
        topic_id: '',
        mode: 'model_paper',
        year: null,
        explanation: '',
        image_url: ''
      });
      handleRemoveImage();
      setAddDialogOpen(false); // Close the dialog
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add question');
    } finally {
      setSubmitting(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${API}/questions/${questionId}`);
      toast.success('Question deleted');
      setSelectedQuestions(prev => prev.filter(id => id !== questionId));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleEditQuestion = (question) => {
    // Ensure options are in the correct format
    const formattedOptions = question.options.map(opt => {
      if (typeof opt === 'string') {
        return { text: opt, image_url: '' };
      }
      return opt;
    });
    
    setEditingQuestion({
      ...question,
      options: formattedOptions,
      stream: 'science' // Default stream
    });
    setEditDialogOpen(true);
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!editingQuestion) return;

    setSubmitting(true);
    try {
      const questionData = {
        text: editingQuestion.text,
        options: editingQuestion.options,
        correct_option: editingQuestion.correct_option,
        difficulty: editingQuestion.difficulty,
        subject_id: editingQuestion.subject_id || editingQuestion.subject,
        topic_id: editingQuestion.topic_id || editingQuestion.topic || null,
        mode: editingQuestion.mode,
        year: editingQuestion.mode === 'past_paper' ? editingQuestion.year : null,
        explanation: editingQuestion.explanation || '',
        image_url: editingQuestion.image_url || null
      };

      await axios.put(`${API}/questions/${editingQuestion.id}`, questionData);
      toast.success('Question updated successfully!');
      setEditDialogOpen(false);
      setEditingQuestion(null);
      fetchData();
    } catch (error) {
      console.error('Update error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOptionChange = (index, field, value) => {
    if (!editingQuestion) return;
    const newOptions = [...editingQuestion.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setEditingQuestion({ ...editingQuestion, options: newOptions });
  };

  const handleEditOptionImageUpload = async (index, file) => {
    if (!file || !editingQuestion) return;
    try {
      const imageUrl = await uploadImage(file);
      handleEditOptionChange(index, 'image_url', imageUrl);
      toast.success(`Option ${index + 1} image uploaded`);
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    }
  };

  const handleAIExtraction = async (file) => {
    if (!file) return;
    
    setExtracting(true);
    setExtractionFile(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      toast.info('🤖 AI is analyzing the question...', { duration: 5000 });
      
      const response = await axios.post(`${API}/questions/extract-from-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const extracted = response.data;
      
      // Convert extracted options to new format
      const formattedOptions = (extracted.options || ['', '', '', '', '']).map(opt => {
        if (typeof opt === 'string') {
          return { text: opt, image_url: '' };
        }
        return opt;
      });
      
      // Auto-fill the form with extracted data
      setNewQuestion({
        text: extracted.text || '',
        options: formattedOptions,
        correct_option: extracted.correct_option || 0,
        difficulty: extracted.difficulty || 'medium',
        subject_id: extracted.subject_id || '',
        topic_id: extracted.topic_id || '',
        mode: 'model_paper',
        year: null,
        explanation: extracted.explanation || '',
        image_url: ''
      });
      
      toast.success('✨ Question extracted! Please review and add.');
      setExtractionFile(null);
      
    } catch (error) {
      console.error('AI extraction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract question. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  const handleExtractionFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAIExtraction(file);
    }
  };

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.text.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSubject = subjectFilter === 'all' || q.subject_id === subjectFilter;
    const matchesDifficulty = difficultyFilter === 'all' || q.difficulty === difficultyFilter;
    const matchesMode = modeFilter === 'all' || q.mode === modeFilter;
    
    return matchesSearch && matchesSubject && matchesDifficulty && matchesMode;
  });

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const selectAllQuestions = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.id));
    }
  };

  const createDailyPaper = async () => {
    if (!paperDate) {
      toast.error('Please select a date');
      return;
    }
    if (selectedQuestions.length !== 50) {
      toast.error(`Please select exactly 50 questions (currently ${selectedQuestions.length} selected)`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/daily-paper`, {
        date: paperDate,
        question_ids: selectedQuestions
      });
      toast.success(`Daily paper created for ${paperDate}`);
      setSelectedQuestions([]);
      setPaperDate('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create daily paper');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSchool = async (e) => {
    e.preventDefault();
    if (!newSchool.name || !newSchool.district) {
      toast.error('Please fill school name and district');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/schools`, newSchool);
      toast.success('School added successfully!');
      setNewSchool({ name: '', district: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add school');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    if (!window.confirm('Are you sure you want to delete this school?')) return;
    try {
      await axios.delete(`${API}/schools/${schoolId}`);
      toast.success('School deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete school');
    }
  };

  // Stream handlers
  const handleAddStream = async (e) => {
    e.preventDefault();
    if (!newStream.name) {
      toast.error('Please fill stream name');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/streams`, newStream);
      toast.success('Stream added successfully!');
      setNewStream({ name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stream');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStream = async (streamId) => {
    if (!window.confirm('Are you sure you want to delete this stream?')) return;
    try {
      await axios.delete(`${API}/streams/${streamId}`);
      toast.success('Stream deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete stream');
    }
  };

  // Subject handlers
  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.name || newSubject.stream_ids.length === 0) {
      toast.error('Please fill subject name and select at least one stream');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/subjects`, newSubject);
      toast.success('Subject added successfully!');
      setNewSubject({ name: '', stream_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subject');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;
    try {
      await axios.delete(`${API}/subjects/${subjectId}`);
      toast.success('Subject deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete subject');
    }
  };

  // Topic handlers
  const handleAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.name || !newTopic.subject_id) {
      toast.error('Please fill topic name and select a subject');
      return;
    }
    setSubmitting(true);
    try {
      // Calculate next order based on existing topics for this subject
      const subjectTopics = topics.filter(t => t.subject_id === newTopic.subject_id);
      const nextOrder = subjectTopics.length > 0 
        ? Math.max(...subjectTopics.map(t => t.order)) + 1 
        : 1;
      
      await axios.post(`${API}/topics`, { ...newTopic, order: nextOrder });
      toast.success('Topic added successfully!');
      setNewTopic({ name: '', subject_id: '', order: 1 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add topic');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;
    try {
      await axios.delete(`${API}/topics/${topicId}`);
      toast.success('Topic deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete topic');
    }
  };

  const handleTopicDragEnd = async (result, subjectId) => {
    if (!result.destination) return;

    const subjectTopics = topics
      .filter(t => t.subject_id === subjectId)
      .sort((a, b) => a.order - b.order);

    const reorderedTopics = Array.from(subjectTopics);
    const [movedItem] = reorderedTopics.splice(result.source.index, 1);
    reorderedTopics.splice(result.destination.index, 0, movedItem);

    // Update orders
    const updates = reorderedTopics.map((topic, index) => ({
      id: topic.id,
      order: index + 1
    }));

    // Optimistically update UI
    setTopics(prevTopics => {
      const otherTopics = prevTopics.filter(t => t.subject_id !== subjectId);
      const updatedSubjectTopics = reorderedTopics.map((topic, index) => ({
        ...topic,
        order: index + 1
      }));
      return [...otherTopics, ...updatedSubjectTopics].sort((a, b) => {
        if (a.subject_id === b.subject_id) return a.order - b.order;
        return a.subject_id.localeCompare(b.subject_id);
      });
    });

    // Send update to backend
    try {
      await axios.put(`${API}/topics/reorder`, { updates });
      toast.success('Topics reordered successfully!');
    } catch (error) {
      toast.error('Failed to reorder topics');
      fetchData(); // Revert on error
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="admin-dashboard">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage questions and daily papers</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="stat-card" data-testid="stat-users">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
                <div className="text-sm text-muted-foreground">Total Students</div>
              </div>
            </div>
          </div>
          <div className="stat-card" data-testid="stat-questions">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <FileQuestion className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_questions || 0}</div>
                <div className="text-sm text-muted-foreground">Total Questions</div>
              </div>
            </div>
          </div>
          <div className="stat-card" data-testid="stat-attempts">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_attempts || 0}</div>
                <div className="text-sm text-muted-foreground">Total Attempts</div>
              </div>
            </div>
          </div>
          <div className="stat-card" data-testid="stat-daily">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.daily_attempts || 0}</div>
                <div className="text-sm text-muted-foreground">Today&apos;s Attempts</div>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="paper" className="space-y-6">
          <TabsList>
            <TabsTrigger value="paper" data-testid="tab-paper">
              <Calendar className="w-4 h-4 mr-2" />
              Daily Paper
            </TabsTrigger>
            <TabsTrigger value="questions" data-testid="tab-questions">
              <BookOpen className="w-4 h-4 mr-2" />
              Questions
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">
              <Target className="w-4 h-4 mr-2" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="schools" data-testid="tab-schools">
              <School className="w-4 h-4 mr-2" />
              Schools
            </TabsTrigger>
          </TabsList>

          {/* Daily Paper Tab */}
          <TabsContent value="paper">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Create Paper */}
              <div className="stat-card">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Create Daily Paper
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <Label>Paper Date</Label>
                    <Input
                      type="date"
                      value={paperDate}
                      onChange={(e) => setPaperDate(e.target.value)}
                      className="mt-1"
                      data-testid="paper-date-input"
                    />
                  </div>
                  
                  <div className="p-4 rounded-lg bg-secondary/50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Selected Questions</span>
                      <span className={`text-lg font-bold ${selectedQuestions.length === 50 ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {selectedQuestions.length}/50
                      </span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${selectedQuestions.length === 50 ? 'bg-green-500' : 'bg-primary'}`}
                        style={{ width: `${(selectedQuestions.length / 50) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={createDailyPaper} 
                  disabled={submitting || selectedQuestions.length !== 50 || !paperDate}
                  className="w-full btn-hover"
                  data-testid="create-paper-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckSquare className="w-4 h-4 mr-2" />}
                  Create Daily Paper
                </Button>

                {selectedQuestions.length > 0 && selectedQuestions.length !== 50 && (
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Select {50 - selectedQuestions.length} more questions
                  </p>
                )}
              </div>

              {/* Existing Papers */}
              <div className="stat-card">
                <h2 className="text-xl font-semibold mb-6">Recent Daily Papers</h2>
                {dailyPapers.length > 0 ? (
                  <div className="space-y-3">
                    {dailyPapers.slice(0, 5).map((paper) => (
                      <div key={paper.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-primary" />
                          <div>
                            <div className="font-medium">{paper.date}</div>
                            <div className="text-xs text-muted-foreground">
                              {paper.question_ids?.length || 50} questions
                            </div>
                          </div>
                        </div>
                        <span className="px-2 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-medium">
                          Active
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No daily papers created yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Question Selection */}
            <div className="stat-card mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Select Questions for Paper</h2>
                <Button variant="outline" size="sm" onClick={selectAllQuestions}>
                  {selectedQuestions.length === questions.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              {questions.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {questions.map((q, index) => (
                    <div 
                      key={q.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all
                        ${selectedQuestions.includes(q.id) 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'}`}
                    >
                      <Checkbox 
                        checked={selectedQuestions.includes(q.id)}
                        onCheckedChange={() => toggleQuestionSelection(q.id)}
                      />
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => toggleQuestionSelection(q.id)}
                      >
                        <p className="text-sm truncate">{q.text}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">
                            {subjects.find(s => s.id === q.subject_id)?.name || 'Unknown Subject'}
                          </span>
                          {q.topic_id && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-500">
                              {topics.find(t => t.id === q.topic_id)?.name || 'Unknown Topic'}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full capitalize
                            ${q.difficulty === 'easy' ? 'bg-green-500/10 text-green-500' :
                              q.difficulty === 'medium' ? 'bg-yellow-500/10 text-yellow-500' :
                              'bg-red-500/10 text-red-500'}`}>
                            {q.difficulty}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full capitalize
                            ${q.mode === 'past_paper' ? 'bg-orange-500/10 text-orange-500' : 'bg-cyan-500/10 text-cyan-500'}`}>
                            {q.mode === 'past_paper' ? `Past Paper${q.year ? ` ${q.year}` : ''}` : 'Model Paper'}
                          </span>
                          {q.image_url && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500">
                              Image
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewQuestion(q);
                        }}
                        className="text-muted-foreground hover:text-primary"
                        data-testid={`preview-existing-${index}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileQuestion className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Add questions first before creating a daily paper</p>
                </div>
              )}
            </div>

            {/* Preview Modal for Existing Questions */}
            <Dialog open={!!previewQuestion} onOpenChange={(open) => !open && setPreviewQuestion(null)}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Question Preview</DialogTitle>
                </DialogHeader>
                {previewQuestion && (
                  <div className="mt-4">
                    {/* Preview Header */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="px-3 py-1 rounded-full bg-secondary text-sm font-medium">
                        {subjects.find(s => s.id === previewQuestion.subject_id)?.name || 'Unknown Subject'}
                      </span>
                      {previewQuestion.topic_id && (
                        <span className="px-3 py-1 rounded-full bg-purple-500/10 text-purple-500 text-sm font-medium">
                          {topics.find(t => t.id === previewQuestion.topic_id)?.name || 'Unknown Topic'}
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize
                        ${previewQuestion.difficulty === 'easy' ? 'bg-green-500/10 text-green-500' :
                          previewQuestion.difficulty === 'medium' ? 'bg-yellow-500/10 text-yellow-500' :
                          'bg-red-500/10 text-red-500'}`}>
                        {previewQuestion.difficulty}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize
                        ${previewQuestion.mode === 'past_paper' ? 'bg-orange-500/10 text-orange-500' : 'bg-cyan-500/10 text-cyan-500'}`}>
                        {previewQuestion.mode === 'past_paper' ? `Past Paper${previewQuestion.year ? ` ${previewQuestion.year}` : ''}` : 'Model Paper'}
                      </span>
                    </div>

                    {/* Question Box */}
                    <div className="bg-card border border-border rounded-xl p-6 mb-6">
                      <h2 className="text-lg font-semibold leading-relaxed">
                        <RichText>{previewQuestion.text}</RichText>
                      </h2>
                      {previewQuestion.image_url && (
                        <div className="mt-4">
                          <CachedImage 
                            src={previewQuestion.image_url} 
                            alt="Question" 
                            className="max-h-48 mx-auto rounded-lg border border-border"
                          />
                        </div>
                      )}
                    </div>

                    {/* Options */}
                    <div className="space-y-3">
                      {previewQuestion.options.map((option, index) => {
                        const optionText = typeof option === 'string' ? option : option.text;
                        const optionImage = typeof option === 'object' ? option.image_url : null;
                        
                        return (
                          <div
                            key={index}
                            className={`w-full p-4 text-left rounded-lg border-2 transition-all
                              ${previewQuestion.correct_option === index 
                                ? 'border-green-500 bg-green-500/10' 
                                : 'border-border bg-card'}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm
                                ${previewQuestion.correct_option === index 
                                  ? 'bg-green-500 text-white' 
                                  : 'bg-secondary'}`}>
                                {String.fromCharCode(65 + index)}
                              </div>
                              <div className="flex-1">
                                {optionText && <div className="text-sm"><RichText>{optionText}</RichText></div>}
                                {optionImage && (
                                  <CachedImage 
                                    src={optionImage} 
                                    alt={`Option ${String.fromCharCode(65 + index)}`}
                                    className="mt-2 max-h-32 rounded border border-border"
                                  />
                                )}
                              </div>
                              {previewQuestion.correct_option === index && (
                                <span className="ml-auto text-xs text-green-500 font-medium">✓ Correct</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Explanation */}
                    {previewQuestion.explanation && (
                      <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Explanation</p>
                        <p className="text-sm text-muted-foreground"><RichText>{previewQuestion.explanation}</RichText></p>
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>


          {/* Questions Tab (merged Add + All Questions) */}
          <TabsContent value="questions">
            <div className="stat-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">All Questions ({filteredQuestions.length})</h2>
                <Button 
                  onClick={() => setAddDialogOpen(true)} 
                  className="btn-hover"
                  data-testid="add-question-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </div>

              {/* Search and Filters */}
              <div className="mb-6 space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filters:</span>
                  </div>

                  <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Subject" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Subjects</SelectItem>
                      {subjects.map(subject => (
                        <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={modeFilter} onValueChange={setModeFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modes</SelectItem>
                      <SelectItem value="model_paper">Model Paper</SelectItem>
                      <SelectItem value="past_paper">Past Paper</SelectItem>
                    </SelectContent>
                  </Select>

                  {(searchQuery || subjectFilter !== 'all' || difficultyFilter !== 'all' || modeFilter !== 'all') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSearchQuery('');
                        setSubjectFilter('all');
                        setDifficultyFilter('all');
                        setModeFilter('all');
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>

              {filteredQuestions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Topic</th>
                        <th>Question</th>
                        <th>Mode</th>
                        <th>Difficulty</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQuestions.map((q, index) => (
                        <tr key={q.id} data-testid={`question-row-${index}`}>
                          <td>
                            <span className="px-2 py-1 rounded-full bg-secondary text-sm">
                              {q.subject_name || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            {q.topic_name ? (
                              <span className="px-2 py-1 rounded-full bg-purple-500/10 text-purple-500 text-sm">
                                {q.topic_name}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td className="max-w-md truncate">{q.text}</td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-sm capitalize
                              ${q.mode === 'past_paper' ? 'bg-orange-500/10 text-orange-500' : 'bg-cyan-500/10 text-cyan-500'}`}>
                              {q.mode === 'past_paper' ? `Past ${q.year || ''}` : 'Model'}
                            </span>
                          </td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-sm capitalize
                              ${q.difficulty === 'easy' ? 'bg-green-500/10 text-green-500' :
                                q.difficulty === 'medium' ? 'bg-yellow-500/10 text-yellow-500' :
                                'bg-red-500/10 text-red-500'}`}>
                              {q.difficulty}
                            </span>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditQuestion(q)}
                                className="text-primary hover:text-primary"
                                data-testid={`edit-question-${index}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteQuestion(q.id)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-question-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileQuestion className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{questions.length === 0 ? 'No questions added yet' : 'No questions match your filters'}</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Curriculum Management Tab (Streams/Subjects/Topics) */}
          <TabsContent value="content">
            <div className="space-y-6">
              {/* Streams */}
              <div className="stat-card">
                <h2 className="text-xl font-semibold mb-6">Streams</h2>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium mb-4">Add New Stream</h3>
                    <form onSubmit={handleAddStream} className="space-y-4">
                      <div>
                        <Label>Stream Name</Label>
                        <Input
                          placeholder="e.g., Bio Science"
                          value={newStream.name}
                          onChange={(e) => setNewStream({ name: e.target.value })}
                          required
                        />
                      </div>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Stream
                      </Button>
                    </form>
                  </div>
                  <div>
                    <h3 className="font-medium mb-4">All Streams ({streams.length})</h3>
                    {streams.length > 0 ? (
                      <div className="space-y-2">
                        {streams.map((stream) => (
                          <div key={stream.id} className="flex items-center justify-between p-3 border rounded">
                            <span>{stream.name}</span>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteStream(stream.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No streams added yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Subjects */}
              <div className="stat-card">
                <h2 className="text-xl font-semibold mb-6">Subjects</h2>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium mb-4">Add New Subject</h3>
                    <form onSubmit={handleAddSubject} className="space-y-4">
                      <div>
                        <Label>Subject Name</Label>
                        <Input
                          placeholder="e.g., Chemistry"
                          value={newSubject.name}
                          onChange={(e) => setNewSubject({ ...newSubject, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Streams</Label>
                        <ReactSelect
                          isMulti
                          options={streams.map(s => ({ value: s.id, label: s.name }))}
                          value={streams.filter(s => newSubject.stream_ids.includes(s.id)).map(s => ({ value: s.id, label: s.name }))}
                          onChange={(selected) => setNewSubject({ ...newSubject, stream_ids: selected ? selected.map(s => s.value) : [] })}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select streams..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              backgroundColor: 'hsl(var(--background))',
                              borderColor: 'hsl(var(--input))',
                              '&:hover': { borderColor: 'hsl(var(--ring))' }
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: 'hsl(var(--popover))',
                              border: '1px solid hsl(var(--border))'
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isFocused ? 'hsl(var(--accent))' : 'hsl(var(--popover))',
                              color: 'hsl(var(--foreground))',
                              '&:active': { backgroundColor: 'hsl(var(--accent))' }
                            }),
                            multiValue: (base) => ({
                              ...base,
                              backgroundColor: 'hsl(var(--accent))'
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              color: 'hsl(var(--accent-foreground))'
                            }),
                            multiValueRemove: (base) => ({
                              ...base,
                              color: 'hsl(var(--accent-foreground))',
                              '&:hover': { backgroundColor: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))' }
                            })
                          }}
                        />
                      </div>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Subject
                      </Button>
                    </form>
                  </div>
                  <div>
                    <h3 className="font-medium mb-4">All Subjects ({subjects.length})</h3>
                    {subjects.length > 0 ? (
                      <div className="space-y-2">
                        {subjects.map((subject) => (
                          <div key={subject.id} className="flex items-center justify-between p-3 border rounded">
                            <div>
                              <div className="font-medium">{subject.name}</div>
                              <div className="text-sm text-muted-foreground">{subject.stream_names?.join(', ') || 'No streams'}</div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSubject(subject.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No subjects added yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Topics */}
              <div className="stat-card">
                <h2 className="text-xl font-semibold mb-6">Topics</h2>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium mb-4">Add New Topic</h3>
                    <form onSubmit={handleAddTopic} className="space-y-4">
                      <div>
                        <Label>Subject</Label>
                        <Select value={newTopic.subject_id} onValueChange={(value) => setNewTopic({ ...newTopic, subject_id: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Subject" />
                          </SelectTrigger>
                          <SelectContent>
                            {subjects.map((subject) => (
                              <SelectItem key={subject.id} value={subject.id}>
                                {subject.name} ({subject.stream_names?.join(', ') || 'No streams'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Topic Name</Label>
                        <Input
                          placeholder="e.g., Atomic Structure"
                          value={newTopic.name}
                          onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
                          required
                        />
                      </div>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Topic
                      </Button>
                    </form>
                  </div>
                  <div>
                    <h3 className="font-medium mb-4">All Topics ({topics.length})</h3>
                    {topics.length > 0 ? (
                      <div className="space-y-6 max-h-96 overflow-y-auto">
                        {subjects.map((subject) => {
                          const subjectTopics = topics
                            .filter(t => t.subject_id === subject.id)
                            .sort((a, b) => a.order - b.order);
                          
                          if (subjectTopics.length === 0) return null;

                          return (
                            <div key={subject.id}>
                              <div className="text-sm font-medium text-muted-foreground mb-2">
                                {subject.name}
                              </div>
                              <DragDropContext onDragEnd={(result) => handleTopicDragEnd(result, subject.id)}>
                                <Droppable droppableId={subject.id}>
                                  {(provided) => (
                                    <div
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                      className="space-y-2"
                                    >
                                      {subjectTopics.map((topic, index) => (
                                        <Draggable key={topic.id} draggableId={topic.id} index={index}>
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              className={`flex items-center gap-2 p-3 border rounded ${
                                                snapshot.isDragging ? 'bg-accent' : 'bg-background'
                                              }`}
                                            >
                                              <div {...provided.dragHandleProps}>
                                                <GripVertical className="w-4 h-4 text-muted-foreground" />
                                              </div>
                                              <div className="flex-1">
                                                <div className="font-medium">Unit {topic.order}: {topic.name}</div>
                                              </div>
                                              <Button variant="ghost" size="sm" onClick={() => handleDeleteTopic(topic.id)}>
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                              </Button>
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No topics added yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Schools Tab */}
          <TabsContent value="schools">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Add School */}
              <div className="stat-card">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <School className="w-5 h-5 text-primary" />
                  Add New School
                </h2>
                <form onSubmit={handleAddSchool} className="space-y-4">
                  <div className="space-y-2">
                    <Label>School Name</Label>
                    <Input
                      placeholder="e.g., Ananda College"
                      value={newSchool.name}
                      onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                      required
                      data-testid="school-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>District</Label>
                    <Select
                      value={newSchool.district}
                      onValueChange={(value) => setNewSchool({ ...newSchool, district: value })}
                    >
                      <SelectTrigger data-testid="school-district-select">
                        <SelectValue placeholder="Select district" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Colombo">Colombo</SelectItem>
                        <SelectItem value="Gampaha">Gampaha</SelectItem>
                        <SelectItem value="Kalutara">Kalutara</SelectItem>
                        <SelectItem value="Kandy">Kandy</SelectItem>
                        <SelectItem value="Matale">Matale</SelectItem>
                        <SelectItem value="Nuwara Eliya">Nuwara Eliya</SelectItem>
                        <SelectItem value="Galle">Galle</SelectItem>
                        <SelectItem value="Matara">Matara</SelectItem>
                        <SelectItem value="Hambantota">Hambantota</SelectItem>
                        <SelectItem value="Jaffna">Jaffna</SelectItem>
                        <SelectItem value="Kilinochchi">Kilinochchi</SelectItem>
                        <SelectItem value="Mannar">Mannar</SelectItem>
                        <SelectItem value="Vavuniya">Vavuniya</SelectItem>
                        <SelectItem value="Mullaitivu">Mullaitivu</SelectItem>
                        <SelectItem value="Batticaloa">Batticaloa</SelectItem>
                        <SelectItem value="Ampara">Ampara</SelectItem>
                        <SelectItem value="Trincomalee">Trincomalee</SelectItem>
                        <SelectItem value="Kurunegala">Kurunegala</SelectItem>
                        <SelectItem value="Puttalam">Puttalam</SelectItem>
                        <SelectItem value="Anuradhapura">Anuradhapura</SelectItem>
                        <SelectItem value="Polonnaruwa">Polonnaruwa</SelectItem>
                        <SelectItem value="Badulla">Badulla</SelectItem>
                        <SelectItem value="Monaragala">Monaragala</SelectItem>
                        <SelectItem value="Ratnapura">Ratnapura</SelectItem>
                        <SelectItem value="Kegalle">Kegalle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full btn-hover" data-testid="add-school-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add School
                  </Button>
                </form>
              </div>

              {/* Schools List */}
              <div className="stat-card">
                <h2 className="text-xl font-semibold mb-6">All Schools ({schools.length})</h2>
                {schools.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {schools.map((school) => (
                      <div 
                        key={school.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <School className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium">{school.name}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {school.district}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSchool(school.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <School className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No schools added yet</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Question Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Question</DialogTitle>
            </DialogHeader>
            {editingQuestion && (
              <form onSubmit={handleUpdateQuestion} className="space-y-6">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Select
                      value={editingQuestion.subject_id}
                      onValueChange={(value) => setEditingQuestion({ ...editingQuestion, subject_id: value, topic_id: '' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map(subject => (
                          <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Topic</Label>
                    <Select
                      value={editingQuestion.topic_id || ''}
                      onValueChange={(value) => setEditingQuestion({ ...editingQuestion, topic_id: value })}
                      disabled={!editingQuestion.subject_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={editingQuestion.subject_id ? "Select topic" : "Select subject first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {topics
                          .filter(t => t.subject_id === editingQuestion.subject_id)
                          .sort((a, b) => a.order - b.order)
                          .map(topic => (
                            <SelectItem key={topic.id} value={topic.id}>{topic.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Difficulty</Label>
                    <Select
                      value={editingQuestion.difficulty}
                      onValueChange={(value) => setEditingQuestion({ ...editingQuestion, difficulty: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Question Mode</Label>
                    <Select
                      value={editingQuestion.mode}
                      onValueChange={(value) => setEditingQuestion({ ...editingQuestion, mode: value, year: value === 'model_paper' ? null : editingQuestion.year })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="model_paper">Model Paper</SelectItem>
                        <SelectItem value="past_paper">Past Paper</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editingQuestion.mode === 'past_paper' && (
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select
                        value={editingQuestion.year?.toString() || ''}
                        onValueChange={(value) => setEditingQuestion({ ...editingQuestion, year: parseInt(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select year" />
                        </SelectTrigger>
                        <SelectContent>
                          {YEARS.map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Question Text</Label>
                  <Textarea
                    placeholder="Enter the question..."
                    value={editingQuestion.text}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
                    className="min-h-[100px]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Question Image (Optional)</Label>
                  {editingQuestion.image_url ? (
                    <div className="space-y-2">
                      <div className="relative border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                        <CachedImage 
                          src={editingQuestion.image_url} 
                          alt="Question" 
                          className="max-h-64 mx-auto rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => setEditingQuestion({ ...editingQuestion, image_url: '' })}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                      <Input
                        placeholder="https://example.com/image.png or paste URL to replace"
                        value={editingQuestion.image_url}
                        onChange={(e) => setEditingQuestion({ ...editingQuestion, image_url: e.target.value })}
                      />
                    </div>
                  ) : (
                    <Input
                      placeholder="https://example.com/image.png or paste image URL"
                      value={editingQuestion.image_url || ''}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, image_url: e.target.value })}
                    />
                  )}
                </div>

                <div className="space-y-4">
                  <Label>Options (check the correct answer)</Label>
                  {editingQuestion.options.map((option, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm bg-secondary">
                          {String.fromCharCode(65 + index)}
                        </div>
                        <Input
                          placeholder={`Option ${String.fromCharCode(65 + index)} (text)`}
                          value={option.text || ''}
                          onChange={(e) => handleEditOptionChange(index, 'text', e.target.value)}
                          className="flex-1"
                        />
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editingQuestion.correct_option === index}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setEditingQuestion({ ...editingQuestion, correct_option: index });
                              }
                            }}
                          />
                          <span className={`text-xs font-medium ${editingQuestion.correct_option === index ? 'text-green-500' : 'text-muted-foreground'}`}>
                            Correct
                          </span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 ml-11">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleEditOptionImageUpload(index, e.target.files[0])}
                          className="flex-1 text-sm"
                        />
                        {option.image_url && (
                          <div className="flex items-center gap-2">
                            <CachedImage src={option.image_url} alt={`Option ${index + 1}`} className="h-12 w-12 object-cover rounded" />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditOptionChange(index, 'image_url', '')}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label>Explanation (Optional)</Label>
                  <Textarea
                    placeholder="Explain the correct answer..."
                    value={editingQuestion.explanation || ''}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation: e.target.value })}
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={submitting} className="btn-hover">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckSquare className="w-4 h-4 mr-2" />}
                    Update Question
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="btn-hover">
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Question Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Question</DialogTitle>
            </DialogHeader>
            
            {/* AI Extraction Section */}
            <div className="border-2 border-dashed border-primary/30 rounded-lg p-6 bg-primary/5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">AI Question Extraction</h3>
                </div>
                <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">Beta</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Upload a screenshot of your question and let AI extract it automatically. Supports Sinhala, Tamil, English with LaTeX formulas!
              </p>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleExtractionFileSelect}
                  className="hidden"
                  id="ai-extraction-upload"
                  disabled={extracting}
                />
                <label htmlFor="ai-extraction-upload">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full btn-hover cursor-pointer"
                    disabled={extracting}
                    asChild
                  >
                    <div>
                      {extracting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          AI is analyzing...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          Upload Question Screenshot
                        </>
                      )}
                    </div>
                  </Button>
                </label>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or enter manually</span>
              </div>
            </div>

            <form onSubmit={handleSubmitQuestion} className="space-y-6">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Select
                    value={newQuestion.subject_id}
                    onValueChange={(value) => setNewQuestion({ ...newQuestion, subject_id: value, topic_id: '' })}
                  >
                    <SelectTrigger data-testid="subject-select">
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map(subject => (
                        <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Topic (Optional)</Label>
                  <Select
                    value={newQuestion.topic_id || ''}
                    onValueChange={(value) => setNewQuestion({ ...newQuestion, topic_id: value })}
                    disabled={!newQuestion.subject_id}
                  >
                    <SelectTrigger data-testid="topic-select">
                      <SelectValue placeholder={newQuestion.subject_id ? "Select topic" : "Select subject first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {topics
                        .filter(t => t.subject_id === newQuestion.subject_id)
                        .sort((a, b) => a.order - b.order)
                        .map(topic => (
                          <SelectItem key={topic.id} value={topic.id}>{topic.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <Select
                    value={newQuestion.difficulty}
                    onValueChange={(value) => setNewQuestion({ ...newQuestion, difficulty: value })}
                  >
                    <SelectTrigger data-testid="difficulty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Question Mode</Label>
                  <Select
                    value={newQuestion.mode}
                    onValueChange={(value) => setNewQuestion({ ...newQuestion, mode: value, year: value === 'model_paper' ? null : newQuestion.year })}
                  >
                    <SelectTrigger data-testid="mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="model_paper">Model Paper</SelectItem>
                      <SelectItem value="past_paper">Past Paper</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newQuestion.mode === 'past_paper' && (
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select
                      value={newQuestion.year?.toString() || ''}
                      onValueChange={(value) => setNewQuestion({ ...newQuestion, year: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="year-select">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Question Text</Label>
                <Textarea
                  placeholder="Enter the question... (supports LaTeX: use $formula$ for inline, $$formula$$ for block)"
                  value={newQuestion.text}
                  onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                  className="min-h-[100px]"
                  required
                  data-testid="question-text"
                />
                <p className="text-xs text-muted-foreground">
                  Supports LaTeX: <code className="bg-secondary px-1 rounded">$x^2$</code> for inline,
                  <code className="bg-secondary px-1 rounded ml-1">$10^{"{-17}"}$</code> for superscript,
                  <code className="bg-secondary px-1 rounded ml-1">$\frac{"{a}{b}"}$</code> for fractions
                </p>
              </div>

              <div className="space-y-2">
                <Label>Question Image <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                {!imagePreview && !newQuestion.image_url ? (
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="image-upload"
                      data-testid="question-image-input"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <Upload className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium mb-1">Click to upload image</p>
                      <p className="text-xs text-muted-foreground">PNG, JPG, GIF up to 5MB</p>
                    </label>
                  </div>
                ) : (
                  <div className="relative border border-border rounded-lg p-4">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 h-8 w-8 p-0 bg-background/80 hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <img 
                      src={imagePreview || newQuestion.image_url} 
                      alt="Preview" 
                      className="max-h-48 mx-auto rounded"
                    />
                    {uploading && uploadProgress > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Uploading...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <Label>Options (check the correct answer)</Label>
                {newQuestion.options.map((option, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm bg-secondary">
                        {String.fromCharCode(65 + index)}
                      </div>
                      <Input
                        placeholder={`Option ${String.fromCharCode(65 + index)} (text)`}
                        value={option.text || ''}
                        onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                        className="flex-1"
                        data-testid={`option-${index}`}
                      />
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={newQuestion.correct_option === index}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewQuestion({ ...newQuestion, correct_option: index });
                            }
                          }}
                          data-testid={`correct-option-${index}`}
                        />
                        <span className={`text-xs font-medium ${newQuestion.correct_option === index ? 'text-green-500' : 'text-muted-foreground'}`}>
                          Correct
                        </span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2 ml-11">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleOptionImageUpload(index, e.target.files[0])}
                        className="flex-1 text-sm"
                      />
                      {option.image_url && (
                        <div className="flex items-center gap-2">
                          <CachedImage src={option.image_url} alt={`Option ${index + 1}`} className="h-12 w-12 object-cover rounded" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOptionChange(index, 'image_url', '')}
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Explanation (Optional)</Label>
                <Textarea
                  placeholder="Explain why this is the correct answer..."
                  value={newQuestion.explanation}
                  onChange={(e) => setNewQuestion({ ...newQuestion, explanation: e.target.value })}
                  data-testid="explanation-input"
                />
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={submitting || uploading} className="btn-hover" data-testid="submit-question-btn">
                  {(submitting || uploading) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Question
                </Button>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" className="btn-hover">
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Question Preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-lg font-semibold leading-relaxed">
                          <RichText>{newQuestion.text || 'Question text will appear here...'}</RichText>
                        </h2>
                        {newQuestion.image_url && (
                          <div className="mt-4">
                            <CachedImage 
                              src={newQuestion.image_url} 
                              alt="Question" 
                              className="max-h-48 mx-auto rounded-lg border border-border"
                            />
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        {newQuestion.options.map((option, index) => (
                          <div
                            key={index}
                            className={`w-full p-4 text-left rounded-lg border-2 transition-all
                              ${newQuestion.correct_option === index 
                                ? 'border-green-500 bg-green-500/10' 
                                : 'border-border bg-card hover:border-primary/50'}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm
                                ${newQuestion.correct_option === index 
                                  ? 'bg-green-500 text-white' 
                                  : 'bg-secondary'}`}>
                                {String.fromCharCode(65 + index)}
                              </div>
                              <span className="text-sm"><RichText>{option || `Option ${String.fromCharCode(65 + index)}`}</RichText></span>
                              {newQuestion.correct_option === index && (
                                <span className="ml-auto text-xs text-green-500 font-medium">✓ Correct</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {newQuestion.explanation && (
                        <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                          <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Explanation</p>
                          <p className="text-sm text-muted-foreground"><RichText>{newQuestion.explanation}</RichText></p>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="btn-hover">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
